import { type SpeechLanguage, speechLanguages } from '@hugo/core'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { queryKeys, useRunTool, useSettings } from '@/lib/items'
import { useSession } from '@/lib/session'
import { canCapture } from '@/lib/voice/capture'

const LANGUAGE_LABELS: Record<SpeechLanguage, string> = { en: 'English', de: 'Deutsch' }

/**
 * Settings → Voice: the language the user dictates in (the speech service needs it), and where
 * the audio goes. Without a speech service on this server (or a browser that records), it says
 * so and has nothing to set.
 */
export function VoiceCard() {
  const { me } = useSession()
  const settings = useSettings()
  const queryClient = useQueryClient()
  const { run } = useRunTool()
  const [saving, setSaving] = useState<SpeechLanguage | null>(null)
  const enabled = me?.voice.enabled ?? false
  const language = saving ?? settings.data?.speechLanguage ?? 'en'

  const change = async (next: SpeechLanguage) => {
    setSaving(next)
    const result = await run(
      'settings.update',
      { speechLanguage: next },
      { done: `You dictate in ${LANGUAGE_LABELS[next]}`, failed: 'Could not change the language' },
    )
    if (result) queryClient.setQueryData(queryKeys.settings, result)
    setSaving(null)
  }

  return (
    <Card data-testid="voice-settings">
      <CardHeader>
        <CardTitle>Voice</CardTitle>
        <CardDescription data-testid="voice-description">
          {!enabled
            ? 'Voice is not available on this server.'
            : canCapture()
              ? 'Press V (or + and the microphone) and say what you need to remember. Your voice goes straight from this device to the speech service; hugo only gets the text, and keeps no audio.'
              : 'This browser cannot record audio. Dictate from another browser or the app.'}
        </CardDescription>
      </CardHeader>
      {enabled ? (
        <CardContent className="flex flex-col gap-1.5">
          <Label htmlFor="speech-language">Language you speak</Label>
          <NativeSelect
            id="speech-language"
            className="w-full sm:w-48"
            value={language}
            disabled={saving !== null || !settings.data}
            onChange={(event) => void change(event.target.value as SpeechLanguage)}
          >
            {speechLanguages.map((code) => (
              <NativeSelectOption key={code} value={code}>
                {LANGUAGE_LABELS[code]}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </CardContent>
      ) : null}
    </Card>
  )
}
